import {GameEvent, isRemovePlayerEvent, Projection, SharedGameState, useGameStore} from '@glark-newco/game-library';
import {useMemo} from 'react';
import {filterTileEvents} from './eventHelpers';
import {isDealTilesEvent, isPlayTileEvent, isRemoveTileEvent, TilePayload} from '../SignlinesPayloadTypes';
import {cols, getColNumber, getRowNumber, rows} from '../utils/GameBoardUtils';

export type TileId = string;

export interface Tile {
    tileId: TileId;
}

export interface BoardCell {
    location: string;
    tileId?: TileId;
    playerId?: string;
}

export interface BoardSlice extends SharedGameState {
    signlinesBoard?: BoardCell[];
}
export interface SignlinesCompletionStats {
    totalTiles: number;
    correctTiles: number;
    incorrectTiles: number;
    isComplete: boolean;
}

function emptyBoard(): BoardCell[] {
    return new Array(cols.length * rows.length).fill(undefined).map((_, i) => {
        const colIdx = i % cols.length;
        const location = cols[colIdx] + String(Math.floor(i / cols.length));
        return {location} as BoardCell;
    });
}


function applyPlayEvent(board: BoardCell[], ev: TilePayload): BoardCell[] {
    if (isDealTilesEvent(ev))
        return board;

    const col = getColNumber(ev.positionId);
    const row = getRowNumber(ev.positionId);
    const idx = row * cols.length + col;

    if (isPlayTileEvent(ev) && board[idx].tileId === undefined) {
        board[idx] = {...board[idx], playerId: ev.playerId, tileId: ev.tileId};
    } else if (isRemoveTileEvent(ev) && board[idx].tileId === ev.tileId) {
        board[idx] = {...board[idx], playerId: undefined, tileId: undefined};
    }
    return board;
}

function replacePlayer(board: BoardCell[], oldId: string, newId?: string): BoardCell[] {
    return board.map(cell => ({
        ...cell,
        playerId: (newId && cell.playerId === oldId) ? newId : cell.playerId,
    }));
}

/**
 * Projection – runs once per tick.
 *   * Update the board based on Tile events.
 *   * If a ReplacePlayerEvent is in `newEvents`, first apply the new tile events,
 *     then walk the whole board and replace the old id with the new one.
 */
export const boardProjection: Projection<BoardSlice> = {
    name: 'boardProjection',
    project: (
        state: BoardSlice,
        newEvents: GameEvent[],
    ): Partial<BoardSlice> => {

        const prevBoard: BoardCell[] = [...(state.signlinesBoard ?? emptyBoard())];
        const tileEvents = filterTileEvents(newEvents);

        const boardAfterTileEvents = tileEvents.reduce<BoardCell[]>(
            (acc: BoardCell[], ev: TilePayload) => applyPlayEvent(acc, ev),
            [...prevBoard],
        );

        // Update PlayerIDs if any players have been replaced.
        const removals = newEvents.filter(isRemovePlayerEvent);
        const finalBoard = removals.reduce<BoardCell[]>(
            (board, r) => r.replacementId
                ? replacePlayer(board, r.removePlayerId, r.replacementId)
                : board,
            boardAfterTileEvents,
        );

        return {
            signlinesBoard: finalBoard,
        };
    },
    reset: () => ({signlinesBoard: undefined}), // optional – clears the board
};

/** Public hook – thin read‑only wrapper */
export const useGameBoard = () =>
    useGameStore((s: BoardSlice) => s.signlinesBoard ?? []);

export function useGameCompletion(): SignlinesCompletionStats {
    const gameBoard = useGameBoard();
    return useMemo(() => {
        const emptyStats = {
            totalTiles: 0,
            correctTiles: 0,
            incorrectTiles: 0,
            isComplete: false,
        } as SignlinesCompletionStats;

        return gameBoard.reduce((partial, cell) => {
            const correctTile = !!cell.tileId && cell.tileId === cell.location;
            const wrongTile = !!cell.tileId && cell.tileId !== cell.location;
            return {
                totalTiles: partial.totalTiles + 1,
                correctTiles: correctTile ? partial.correctTiles + 1 : partial.correctTiles,
                incorrectTiles: wrongTile ? partial.incorrectTiles + 1 : partial.incorrectTiles,
                isComplete: correctTile && partial.correctTiles === partial.totalTiles,
            } as SignlinesCompletionStats;
        }, emptyStats);
    }, [gameBoard]);
}
